/* Bodi Product Scene — sync the hero colour + buy-box scent detail to the
   selected scent variant. Decoupled from Dawn internals: listens for `change`
   on the surrounding <product-info> and matches the checked option value
   against the scene data map (keyed by lowercased scent name). Survives Dawn's
   variant re-render; reduced-motion safe. */
(function () {
  if (customElements.get('bodi-scent-scene')) return;

  class BodiScentScene extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-bps-stage]');
      this.sceneLayer = this.querySelector('[data-bps-scene]');
      this.productImg = this.querySelector('[data-bps-product]');
      this.swapProduct = this.getAttribute('data-swap-product') === 'true';
      this.data = this._readData();
      this.host = this.closest('product-info') || document;
      this.colourTarget = this.closest('product-info') || this.stage;

      // buy-box scent detail panel (rendered by a custom_liquid block)
      this.detail = this.host.querySelector('[data-bps-detail]');
      this.detailBlurb = this.host.querySelector('[data-bps-detail-blurb]');
      this.detailNotes = this.host.querySelector('[data-bps-detail-notes]');

      this._setupCardImages();

      // Dawn re-renders the variant picker on change (Section Rendering API),
      // which discards the JS-injected photo swatches — re-apply on re-render.
      if (this.host && this.host.nodeType === 1) {
        this._mo = new MutationObserver(() => {
          clearTimeout(this._moT);
          this._moT = setTimeout(() => this._setupCardImages(), 40);
        });
        this._mo.observe(this.host, { childList: true, subtree: true });
      }

      this._onChange = this._onChange.bind(this);
      this.host.addEventListener('change', this._onChange);

      // build-your-pack selector previews scents via a custom event
      this._onPreview = (e) => {
        if (e.detail && e.detail.key && this.data[e.detail.key]) {
          this._apply(e.detail.key, false);
        }
      };
      document.addEventListener('bodi:scent-preview', this._onPreview);

      const current = this._currentScent();
      if (current) this._apply(current, true);
    }

    disconnectedCallback() {
      if (this.host) this.host.removeEventListener('change', this._onChange);
      document.removeEventListener('bodi:scent-preview', this._onPreview);
      if (this._mo) this._mo.disconnect();
    }

    _readData() {
      const tag = this.querySelector('[data-bps-data]');
      if (!tag) return {};
      try { return JSON.parse(tag.textContent.trim()); } catch (e) { return {}; }
    }

    _dataFor(text) {
      const t = (text || '').trim().toLowerCase();
      for (const key in this.data) {
        if (t.indexOf(key) === 0) return this.data[key];
      }
      return null;
    }

    /* give each scent card a photo swatch when an image exists for it */
    _setupCardImages() {
      const labels = this.host.querySelectorAll(
        '.product-form__input--pill input[type="radio"] + label'
      );
      labels.forEach((label) => {
        const d = this._dataFor(label.textContent);
        if (!d) return;
        const img = d.productImage || d.image;
        if (img) {
          label.style.setProperty('--bps-swatch-img', "url('" + img + "')");
          label.classList.add('has-swatch-img');
        } else if (d.top && d.bottom) {
          // no photo yet — tint the tile with the scent's own ground colour so
          // it matches the hero when selected (instead of a position-based hue)
          label.style.setProperty(
            '--bps-swatch-colour',
            'linear-gradient(165deg, ' + d.top + ', ' + d.bottom + ')'
          );
          label.classList.add('has-swatch-colour');
        }
      });
    }

    _currentScent() {
      const candidates = [];
      this.host
        .querySelectorAll('input[type="radio"]:checked, select')
        .forEach((el) => {
          if (el.tagName === 'SELECT') {
            const opt = el.options[el.selectedIndex];
            if (opt) candidates.push(opt.value, opt.textContent);
          } else {
            candidates.push(el.value);
          }
        });
      for (const c of candidates) {
        if (!c) continue;
        const key = c.trim().toLowerCase();
        if (this.data[key]) return key;
      }
      return null;
    }

    _onChange() {
      const key = this._currentScent();
      if (key) this._apply(key, false);
    }

    _apply(key, immediate) {
      const d = this.data[key];
      if (!d) return;

      const commit = () => {
        if (this.colourTarget) {
          if (d.top) this.colourTarget.style.setProperty('--bps-top', d.top);
          if (d.bottom) this.colourTarget.style.setProperty('--bps-bottom', d.bottom);
        }
        if (this.sceneLayer) {
          if (d.image) {
            this.stage.style.setProperty('--bps-img', "url('" + d.image + "')");
            this.sceneLayer.classList.add('has-img');
          } else {
            this.stage.style.removeProperty('--bps-img');
            this.sceneLayer.classList.remove('has-img');
          }
        }
        if (this.swapProduct && this.productImg && d.productImage) {
          this.productImg.src = d.productImage;
        }
        this._updateDetail(d, immediate);
      };

      const reduce =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (immediate || reduce || !this.stage) {
        commit();
        return;
      }

      this.stage.classList.add('is-swapping');
      window.setTimeout(() => {
        commit();
        this.stage.classList.remove('is-swapping');
      }, 200);
    }

    _updateDetail(d, immediate) {
      if (this.detailBlurb) this.detailBlurb.textContent = d.blurb || '';
      if (this.detailNotes) this.detailNotes.textContent = d.notes || '';
      if (!this.detail) return;
      const hasContent = !!(d.blurb || d.notes);
      this.detail.hidden = !hasContent;
      if (hasContent && !immediate) {
        // re-trigger the drop-in animation
        this.detail.classList.remove('is-revealing');
        void this.detail.offsetWidth;
        this.detail.classList.add('is-revealing');
      }
    }
  }

  customElements.define('bodi-scent-scene', BodiScentScene);
})();
